/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@emotion/react";
import MUIDataTable from "mui-datatables";
import { getData } from "../../Services/getData";
import { localUrlEnum, urlEnum } from "../../Utils/urlEnum";
import { Vocabulary } from "../../Utils/Vocabulary";
import { getMuiTheme } from "../../Utils/globalStyleTables";
import { tableLayoutOnMobile } from "../../Utils/Constants";
import { isMobile } from "../../Utils/utils";
import XAutoFiltersComponent, { XAutoFilters } from "./XAutoFilters";
import withRole from "../../Utils/withRole";
import formatDate, { europeanDateWithTimePM } from "../../Utils/formatDate";
import {
  BlockOutlined,
  CheckCircleOutline,
  PersonOutline,
} from "@mui/icons-material";
import { Button, Grid } from "@mui/material";
import GenericModal from "../../Modal/GenericModal";
import { updateData } from "../../Services/updateData";
import { VehicleModel } from "../../Models/VehicleModel";

function renderUserCell(userName: string): JSX.Element {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <PersonOutline style={{ fontSize: 16, color: "#888" }} />
      <span style={{ fontSize: "0.875rem" }}>{userName ?? "-"}</span>
    </div>
  );
}

type XAutoState = {
  vehicles: VehicleModel[];
  total: number;
};

const STATUS_ACTIVE = "active";
const STATUS_DEACTIVATED = "deactivated";

const FILTER_KEYS: (keyof Omit<XAutoFilters, "page" | "perPage">)[] = [
  "brandId",
  "modelId",
  "minPrice",
  "maxPrice",
];

function getStatusFromPath(pathname: string): string {
  if (pathname.includes("/deactivated")) return STATUS_DEACTIVATED;
  return STATUS_ACTIVE;
}

function XAutoTable() {
  const location = useLocation();
  const navigate = useNavigate();
  const status = getStatusFromPath(location.pathname);

  const [state, setState] = useState<XAutoState>({
    vehicles: [],
    total: 0,
  });

  type ActionType = "activate" | "deactivate";
  const [actionModal, setActionModal] = useState<{
    open: boolean;
    vehicleId: number | null;
    action: ActionType;
  }>({ open: false, vehicleId: null, action: "deactivate" });

  /**
   * Opens the action modal for activate/deactivate vehicle
   * @param {number} vehicleId - Id of the vehicle to activate/deactivate
   * @param {ActionType} action - Action to take on the vehicle (activate/deactivate)
   */
  const openActionModal = (vehicleId: number, action: ActionType) =>
    setActionModal({ open: true, vehicleId, action });

  /**
   * Close the action modal
   */
  const closeActionModal = () =>
    setActionModal((prev) => ({ ...prev, open: false, vehicleId: null }));

  /**
   * Handle confirm action for activate/deactivate vehicle
   * @returns {void}
   */
  const handleConfirmAction = () => {
    if (!actionModal.vehicleId) return;
    const url =
      actionModal.action === "deactivate"
        ? urlEnum.xautoDeactivate
        : urlEnum.xautoActivate;
    updateData(url, { vehicleId: actionModal.vehicleId }).then(() => {
      closeActionModal();
      fetchVehicles(filters);
    });
  };

  const [filters, setFilters] = useState<XAutoFilters>({
    page: 0,
    perPage: 15,
    brandId: null,
    modelId: null,
    minPrice: null,
    maxPrice: null,
  });

  useEffect(() => {
    loadData();
  }, [location]);

  const loadData = () => {
    const searchParams = new URLSearchParams(location.search);
    const newFilters: XAutoFilters = {
      page: parseInt(searchParams.get("page") || "0"),
      perPage: parseInt(searchParams.get("perPage") || "10"),
      ...(Object.fromEntries(
        FILTER_KEYS.map((key) => [key, searchParams.get(key)]),
      ) as Pick<XAutoFilters, (typeof FILTER_KEYS)[number]>),
    };
    setFilters(newFilters);
    fetchVehicles(newFilters);
  };

  /**
   * Fetches the list of vehicles from the API based on the given filters.
   * @param {XAutoFilters} currentFilters - The filters to apply to the vehicles list.
   * @returns {void}
   */
  const fetchVehicles = (currentFilters: XAutoFilters) => {
    const queryParams = new URLSearchParams();
    queryParams.append("status", status);
    queryParams.append("page", currentFilters.page.toString());
    queryParams.append("perPage", currentFilters.perPage.toString());
    FILTER_KEYS.forEach((key) => {
      if (currentFilters[key])
        queryParams.append(key, currentFilters[key] as string);
    });

    getData(`${urlEnum.xautoPublished}?${queryParams.toString()}`).then(
      (res) => {
        setState({
          vehicles: res?.data?.data || [],
          total: res?.data?.total || 0,
        });
      },
    );
  };

  const handleFilterChange = (updates: Partial<XAutoFilters>) => {
    const newFilters = { ...filters, ...updates, page: 0 };
    updateFiltersAndNavigate(newFilters);
  };

  const handleResetFilters = () => {
    const resetFilters: XAutoFilters = {
      ...filters,
      ...(Object.fromEntries(FILTER_KEYS.map((key) => [key, null])) as Pick<
        XAutoFilters,
        (typeof FILTER_KEYS)[number]
      >),
      page: 0,
    };
    updateFiltersAndNavigate(resetFilters);
  };

  const updateFiltersAndNavigate = (newFilters: XAutoFilters) => {
    const basePath =
      status === STATUS_DEACTIVATED
        ? localUrlEnum.xautoVehiclesDeactivated
        : localUrlEnum.xautoVehiclesActive;

    const params = new URLSearchParams();
    params.append("page", newFilters.page.toString());
    params.append("perPage", newFilters.perPage.toString());
    FILTER_KEYS.forEach((key) => {
      if (newFilters[key]) params.append(key, newFilters[key] as string);
    });

    navigate(`${basePath}?${params.toString()}`);
  };

  type ColumnVisibility = "both" | "active" | "deactivated";
  type XAutoColumn = {
    name: string;
    label: string;
    statusVisibility?: ColumnVisibility;
    options?: Record<string, any>;
  };

  const allColumns: XAutoColumn[] = [
    {
      name: "id",
      label: "ID",
      statusVisibility: "both",
      options: { filter: false, sort: false, display: false, download: false },
    },
    {
      name: "marca",
      label: Vocabulary.vehicle,
      statusVisibility: "both",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex: number) => {
          const vehicle = state.vehicles[dataIndex];
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <img
                src={`/api/${vehicle.main_image}`}
                alt=""
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "/placeholder_image.png";
                }}
                style={{
                  width: 70,
                  height: 50,
                  objectFit: "cover",
                  borderRadius: 4,
                  flexShrink: 0,
                }}
              />
              <div>
                <div style={{ fontWeight: 600, fontSize: "0.875rem" }}>
                  {`${vehicle?.brand_r?.nume ?? ""} ${vehicle?.model_r?.nume ?? ""} ${vehicle?.variant ?? ""}`.trim()}
                </div>
                <div
                  style={{ color: "#888", fontSize: "0.75rem", marginTop: 2 }}
                >
                  {vehicle?.vin ?? "-"}
                </div>
              </div>
            </div>
          );
        },
      },
    },
    {
      name: "selling_price_euro",
      label: `${Vocabulary.salePrice} (EUR)`,
      statusVisibility: "both",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any) =>
          value != null ? `${value.toLocaleString()} €` : "-",
      },
    },
    {
      name: "xauto_advert_data",
      label: Vocabulary.addedBy,
      statusVisibility: "active",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any) => renderUserCell(value?.synced_by_name),
      },
    },
    {
      name: "xauto_advert_data",
      label: Vocabulary.deactivatedBy,
      statusVisibility: "deactivated",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any) =>
          renderUserCell(value?.deactivated_by_name),
      },
    },
    {
      name: "xauto_advert_data",
      label: Vocabulary.createdAt,
      statusVisibility: "active",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any) => {
          const date = value?.synced_at;
          return date ? formatDate(date, europeanDateWithTimePM) : "-";
        },
      },
    },
    {
      name: "xauto_advert_data",
      label: Vocabulary.deactivatedAt,
      statusVisibility: "deactivated",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any) => {
          const date = value?.deactivated_at;
          return date ? formatDate(date, europeanDateWithTimePM) : "-";
        },
      },
    },
    {
      name: "id",
      label: Vocabulary.actions,
      statusVisibility: "both",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (vehicleId: any) => {
          const isActive = status === STATUS_ACTIVE;
          return (
            <Button
              size="small"
              variant="contained"
              startIcon={
                isActive ? (
                  <BlockOutlined fontSize="small" />
                ) : (
                  <CheckCircleOutline fontSize="small" />
                )
              }
              onClick={(e) => {
                e.stopPropagation();
                openActionModal(
                  vehicleId,
                  isActive ? "deactivate" : "activate",
                );
              }}
              style={{
                backgroundColor: isActive ? "#fdecea" : "#e8f5e9",
                color: isActive ? "#c62828" : "#2e7d32",
                textTransform: "none",
                whiteSpace: "nowrap",
                fontWeight: 600,
                fontSize: "0.75rem",
                borderRadius: 20,
                padding: "4px 14px",
                boxShadow: "none",
              }}
            >
              {isActive ? Vocabulary.deactivate : Vocabulary.activate}
            </Button>
          );
        },
      },
    },
  ];

  const columns = allColumns
    .filter(
      ({ statusVisibility = "both" }) =>
        statusVisibility === "both" || statusVisibility === status,
    )
    .map(({ statusVisibility: _, ...col }) => col);

  const options = {
    filter: false,
    download: false,
    viewColumns: false,
    responsive: tableLayoutOnMobile,
    rowsPerPageOptions: [10, 25, 50, 100],
    sort: false,
    selectableRows: "none" as any,
    print: false,
    page: filters.page,
    serverSide: true,
    rowsPerPage: filters.perPage,
    search: false,
    count: state.total,
    textLabels: {
      body: { noMatch: Vocabulary.noResultsFound },
      pagination: Vocabulary.pagination,
      toolbar: {
        downloadCsv: Vocabulary.downloadExcel,
        viewColumns: Vocabulary.selectColumns,
      },
    },
    onRowClick: (_rowData: string[], rowMeta: { dataIndex: number }) => {
      const vehicle = state.vehicles[rowMeta.dataIndex];
      if (vehicle?.id) {
        window.open(
          `/vehiclesDashboard/vehicleDetails/${vehicle.id}`,
          "_blank",
        );
      }
    },
    setRowProps: () => ({ style: { cursor: "pointer" } }),
    onChangePage: (page: number) => {
      updateFiltersAndNavigate({ ...filters, page });
    },
    onChangeRowsPerPage: (numberOfRows: number) => {
      updateFiltersAndNavigate({ ...filters, perPage: numberOfRows, page: 0 });
    },
  };

  const isDeactivating = actionModal.action === "deactivate";
  const actionVehicle =
    state.vehicles.find((v) => v.id === actionModal.vehicleId) ?? null;
  const actionVehicleName = actionVehicle
    ? `${actionVehicle.brand_r?.nume ?? ""} ${actionVehicle.model_r?.nume ?? ""} ${actionVehicle.variant ?? ""}`.trim()
    : "";

  return (
    <div style={{ padding: isMobile() ? 10 : 20 }}>
      <ThemeProvider theme={getMuiTheme()}>
        <XAutoFiltersComponent
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
        />
        <MUIDataTable
          title=""
          data={state.vehicles}
          columns={columns}
          options={options}
        />
      </ThemeProvider>

      <GenericModal
        open={actionModal.open}
        onClose={closeActionModal}
        title={isDeactivating ? Vocabulary.deactivate : Vocabulary.activate}
      >
        <p style={{ textAlign: "center", fontSize: 18 }}>
          {isDeactivating
            ? Vocabulary.deactivateConfirmMessage
            : Vocabulary.activateConfirmMessage}
        </p>
        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 16 }}>
            {actionVehicleName}
          </div>
          {actionVehicle?.vin && (
            <div style={{ color: "#888", fontSize: 13, marginTop: 4 }}>
              VIN: {actionVehicle.vin}
            </div>
          )}
        </div>
        <Grid container spacing={2} style={{ padding: 10 }}>
          <Grid item xs={6}>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={closeActionModal}
            >
              {Vocabulary.no}
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              variant="contained"
              fullWidth
              style={{
                color: "white",
                backgroundColor: isDeactivating ? "#e53935" : "#43a047",
              }}
              onClick={handleConfirmAction}
            >
              {Vocabulary.yes}
            </Button>
          </Grid>
        </Grid>
      </GenericModal>
    </div>
  );
}

export default withRole(XAutoTable);
